interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, pageSize, totalCount, onPageChange }: PaginationProps) {
  const totalPages = Math.ceil(totalCount / pageSize);

  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <nav
      aria-label="Pagination"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 0',
      }}
    >
      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0 }}>
        Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of{' '}
        {totalCount.toLocaleString()} results
      </p>
      <div style={{ display: 'flex', gap: '0.25rem' }}>
        <button
          className="secondary"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
          style={{ padding: '0.4rem 0.6rem' }}
        >
          ‹
        </button>
        {start > 1 && (
          <>
            <button
              className="secondary"
              onClick={() => onPageChange(1)}
              style={{ padding: '0.4rem 0.6rem' }}
            >
              1
            </button>
            {start > 2 && <span style={{ padding: '0.4rem', color: 'var(--color-text-muted)' }}>…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            className={p === page ? undefined : 'secondary'}
            onClick={() => onPageChange(p)}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
            style={{ padding: '0.4rem 0.6rem' }}
          >
            {p}
          </button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && (
              <span style={{ padding: '0.4rem', color: 'var(--color-text-muted)' }}>…</span>
            )}
            <button
              className="secondary"
              onClick={() => onPageChange(totalPages)}
              style={{ padding: '0.4rem 0.6rem' }}
            >
              {totalPages}
            </button>
          </>
        )}
        <button
          className="secondary"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
          style={{ padding: '0.4rem 0.6rem' }}
        >
          ›
        </button>
      </div>
    </nav>
  );
}
